import React, { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { SIGNIN_TOKEN, USER_REGISTRATION_PATHS } from "./constants";
import { SimplePersistence } from "./utils";
import style from "./styles/common.module.scss";
import { RenderSession } from "./renderSession";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "./components";
import { RequireAuth } from "./routes/RequireAuth";
import { Menu } from "./components/core/Menu/menu";
import { Header } from "./components/secondary/Header";
import { useHeaderConfig } from "./hooks/useHeaderConfig";
import { closeMenu } from "./app/menuSlice";
import ProtectedRoute from "./routes/ProtectedRoute";
import { HomeContentViewer } from "./components/core/Home/HomeContentViewer";
import { SkinHealthHome } from "./components/core/Home/SkinHealthHome";
import RiskAssessment from "./components/core/Home/RiskAssessment/RiskAssessment";
import { PERMISSIONS } from "./constants/permissions";

// const Home = lazy(() => import("./containers/home").then((module) => ({ default: module.Home })));
const LearnMore = lazy(() => import("./containers/learnMore").then((module) => ({ default: module.LearnMore })));
const UnAuthorizedContent = lazy(() => import("./containers/unAuthorizedContent").then((module) => ({ default: module.UnAuthorizedContent })));

const PatientsRoutes = lazy(() => import(/* webpackChunkName: "patients-route" */ "./routes/PatientsRoutes"));
const AdminRoutes = lazy(() => import(/* webpackChunkName: "admin-route" */ "./routes/AdminRoutes"));
const WoundReportRoutes = lazy(() => import(/* webpackChunkName: "wound-report-route" */ "./routes/WoundReportRoutes"));
const BiReportRoutes = lazy(() => import(/* webpackChunkName: "bi-report-route" */ "./routes/BiReportRoutes"));
const TreatmentReportRoutes = lazy(() => import(/* webpackChunkName: "treatment-report-route" */ "./routes/TreatmentReportRoutes"));
const QualityReportRoutes = lazy(() => import(/* webpackChunkName: "quality-report-route" */ "./routes/QualityReportRoutes"));
const PlatformAnalyticsRoutes = lazy(() => import(/* webpackChunkName: "platform-analytics-route" */ "./routes/PlatformAnalyticsRoutes"));

const Login = lazy(() => import("./containers/login"));
const Registration = lazy(() => import("./containers/registration"));
const ResetPassword = lazy(() => import("./containers/resetPassword"));
const ForgotPassword = lazy(() => import("./containers/forgotPassword"));
const UserActivation = lazy(() => import("./containers/userActivation"));
const UpdatePassword = lazy(() => import("./containers/updatePassword"));

const WorkList = lazy(() => import("./containers/worklist"));
const UserProfile = lazy(() => import("./containers/userProfile"));
const Status = lazy(() => import("./containers/status"));

const storage = new SimplePersistence();
const isSignedIn = (user) => user && user.isSignedIn && !!storage.getItem(SIGNIN_TOKEN);

const RenderRoutes = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const menuOpen = useSelector((state) => state.menu.menuOpen);
  const [resetSession, setResetSession] = useState(false);

  const config = useHeaderConfig();

  const showNav = isSignedIn(user) && !USER_REGISTRATION_PATHS.includes(location.pathname);
  useEffect(() => {
    if (showNav && user?.userDetails?.email && !resetSession) {
      setResetSession(true);
    }
  }, [showNav, user, resetSession]);
  return (
    <Suspense fallback={<Loader />}>
      <div className={`container-fluid ${style.medline_block}`}>
        <div className="row p-0">
          {showNav && (
            <>
              {resetSession && <RenderSession />}

              <div
                id="check_left_block"
                className={`${style.left_block}`}
                style={{
                  display: menuOpen ? "block" : "none",
                  transition: "all 0.1s ease-in-out",
                }}
              >
                <Menu user={user} />
              </div>
            </>
          )}
          <div className={`${style.right_block}`} id={"check_right_block"} onClick={() => dispatch(closeMenu())}>
            {!config?.hideHeader && isSignedIn(user) && <Header {...config} />}
            <Routes>
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <SkinHealthHome />
                  </RequireAuth>
                }
              />

              <Route path="/login" element={<Login />} />
              <Route path="/registration" element={<Registration />} />
              <Route path="/resetpassword" element={<ResetPassword />} />
              <Route path="/forgotpassword" element={<ForgotPassword />} />
              <Route path="/useractivation" element={<UserActivation />} />
              <Route path="/updatepassword" element={<UpdatePassword />} />
              <Route path="/createpassword" element={<ResetPassword />} />
              <Route path="/content" element={<HomeContentViewer />} />
              <Route path="/riskassessment" element={<RiskAssessment />} />

              <Route
                path="/worklist"
                element={
                  <RequireAuth>
                    <ProtectedRoute permissionKey={PERMISSIONS.WORKLIST_ROUTE}>
                      <WorkList />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />

              <Route
                path="/userprofile"
                element={
                  <RequireAuth>
                    <UserProfile />
                  </RequireAuth>
                }
              />

              <Route path="/status/:type" element={<Status />} />

              <Route
                path="/patients/*"
                element={
                  <ProtectedRoute permissionKey={PERMISSIONS.PATIENT_ROUTE}>
                    <PatientsRoutes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute permissionKey={PERMISSIONS.ADMIN_ROUTE}>
                    <AdminRoutes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/woundReport/*"
                element={
                  <ProtectedRoute permissionKey={PERMISSIONS.REPORT_ROUTE}>
                    <WoundReportRoutes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/biReports/*"
                element={
                  <ProtectedRoute permissionKey={PERMISSIONS.REPORT_ROUTE}>
                    <BiReportRoutes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/treatmentReport/*"
                element={
                  <ProtectedRoute permissionKey={PERMISSIONS.REPORT_ROUTE}>
                    <TreatmentReportRoutes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/qualityReport/*"
                element={
                  <ProtectedRoute permissionKey={PERMISSIONS.REPORT_ROUTE}>
                    <QualityReportRoutes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/platformanalytics/*"
                element={
                  <ProtectedRoute permissionKey={PERMISSIONS.ADMIN_PLATFORM_ANALYTICS}>
                    <PlatformAnalyticsRoutes />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/learnMore"
                element={
                  <RequireAuth>
                    <LearnMore />
                  </RequireAuth>
                }
              />
              <Route
                path="/unAuthorized"
                element={
                  <RequireAuth>
                    <UnAuthorizedContent />
                  </RequireAuth>
                }
              />
              <Route path="*" element={<UnAuthorizedContent />} />
            </Routes>
          </div>
        </div>
      </div>
    </Suspense>
  );
};

export default RenderRoutes;
